import { useEffect, useRef, useCallback } from "react";
import { useFaust } from "../../contexts/FaustContext";
import { faustLangRegister } from "../../monaco-faust/register";
import editorWorker from "monaco-editor/esm/vs/editor/editor.worker?worker";
import { docSections, faustDocURL, faustSyntaxURL } from "../../documentation";

// Set up Monaco worker
(self as any).MonacoEnvironment = {
    getWorker() {
        return new editorWorker();
    },
};

export function MonacoEditor() {
    const containerRef = useRef<HTMLDivElement>(null);
    const editorInstanceRef = useRef<any>(null);
    const { editorRef, libFaustRef, runDsp, updateDiagram, compileOptionsRef, dispatch } = useFaust();

    // Store latest callbacks in refs so the editor init effect doesn't re-run
    const runDspRef = useRef(runDsp);
    const dispatchRef = useRef(dispatch);
    useEffect(() => { runDspRef.current = runDsp; }, [runDsp]);
    useEffect(() => { dispatchRef.current = dispatch; }, [dispatch]);

    // Init editor exactly once when the container mounts and libFaust is ready
    const libFaustReady = !!libFaustRef.current;

    useEffect(() => {
        if (!containerRef.current || editorInstanceRef.current || !libFaustReady) return;

        let disposed = false;

        (async () => {
            const monaco = await import("monaco-editor");
            const { initVimMode } = await import("monaco-vim");

            if (disposed || !containerRef.current) return;

            const libFaust = libFaustRef.current;
            if (!libFaust) return;

            const { faustLang, providers } = await faustLangRegister(monaco, libFaust);

            // Try to restore saved code
            const defaultCode = `import("stdfaust.lib");\nprocess = ba.pulsen(1, 10000) : pm.djembe(60, 0.3, 0.4, 1) <: dm.freeverb_demo;`;
            let code = defaultCode;
            try {
                const savedParams = localStorage.getItem("faust_editor_params");
                if (savedParams && JSON.parse(savedParams).saveCode) {
                    code = localStorage.getItem("faust_editor_code") || defaultCode;
                }
            } catch {}

            const editor = monaco.editor.create(containerRef.current, {
                value: code,
                language: "faust",
                theme: "vs-dark",
                dragAndDrop: true,
                mouseWheelZoom: true,
                wordWrap: "on",
                minimap: { enabled: false },
                fontSize: 13,
                fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
                fontLigatures: true,
                lineNumbers: "on",
                renderLineHighlight: "line",
                scrollBeyondLastLine: false,
                padding: { top: 8 },
                smoothScrolling: true,
                cursorBlinking: "smooth",
                cursorSmoothCaretAnimation: "on",
                bracketPairColorization: { enabled: true },
            });

            editorInstanceRef.current = editor;
            editorRef.current = editor;

            // Vim mode toggle
            let vimMode: any = null;
            editor.addAction({
                id: "monaco-vim",
                label: "Toggle Vim Mode",
                keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyMod.Shift | monaco.KeyCode.KeyV],
                run: () => {
                    if (vimMode) {
                        vimMode.dispose();
                        vimMode = null;
                    } else {
                        vimMode = initVimMode(editor, null);
                    }
                },
            });

            // Ctrl+R to run -- access via ref so this closure never goes stale
            editor.addAction({
                id: "faust-run",
                label: "Run DSP",
                keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyR],
                run: () => {
                    const code = editor.getValue();
                    runDspRef.current(code).then((result) => {
                        if (result.success) {
                            dispatchRef.current({ type: "SET_ACTIVE_BOTTOM_TAB", tab: "dsp" });
                        }
                    });
                },
            });

            // Ctrl+D for docs
            editor.addAction({
                id: "faust-docs",
                label: "Open Documentation",
                keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyD],
                run: () => {
                    const matched = faustLang.matchDocKey(providers.docs, editor.getModel(), editor.getPosition());
                    let docUrl = faustDocURL;
                    if (matched) {
                        const prefix = matched.nameArray.slice();
                        prefix.pop();
                        const doc = matched.doc;
                        docUrl = `${faustDocURL}/${docSections[prefix.toString().slice(0, 2) as keyof typeof docSections]}/#${prefix.join(".")}${doc.name.replace(/[[\]|]/g, "").toLowerCase()}`;
                    }
                    window.open(docUrl, "_blank");
                },
            });

            // Auto-save on content change
            editor.getModel()?.onDidChangeContent(() => {
                const code = editor.getValue();
                if (compileOptionsRef.current.saveCode) {
                    localStorage.setItem("faust_editor_code", code);
                }
            });

            // Resize observer
            const resizeObserver = new ResizeObserver(() => {
                editor.layout();
            });
            resizeObserver.observe(containerRef.current);

            return () => {
                resizeObserver.disconnect();
            };
        })();

        return () => {
            disposed = true;
            if (editorInstanceRef.current) {
                editorInstanceRef.current.dispose();
                editorInstanceRef.current = null;
                editorRef.current = null;
            }
        };
    // Only depend on stable values -- libFaustReady flips once from false->true
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [libFaustReady]);

    // Handle file drops
    const handleDrop = useCallback(
        (e: React.DragEvent) => {
            e.preventDefault();
            const file = e.dataTransfer?.files?.[0];
            if (!file) return;
            const reader = new FileReader();
            reader.onload = () => {
                if (editorRef.current) {
                    editorRef.current.setValue(reader.result?.toString() || "");
                }
            };
            reader.readAsText(file);
        },
        [editorRef]
    );

    return (
        <div
            ref={containerRef}
            className="absolute inset-0"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
        />
    );
}
